import {msAPI, osAPI, manageAPI} from "./index.js";

export const getMerchants = async (page, limit, storeTypeParam = 'direct', prospect = false, opts = {}) => {
  const params = { page, limit, full: true };
  if (storeTypeParam) params.storeType = storeTypeParam;
  if (prospect) {
    params.prospect = 1;
  }
  if (typeof opts.onboarding === "boolean") {
    params.onboarding = opts.onboarding ? 1 : 0;
  }
  if (opts.onboarding_step) {
    params.onboarding_step = opts.onboarding_step;
  }
  if (opts.sort) {
    params.sort = opts.sort;
  }
  return (await msAPI.get("merchant", {
    params,
  })).data;
};

export const getMerchantByID = async (id) => {
  return (await msAPI.get(`merchant/admin/merchant/${id}`)).data;
}

export const getStripeCoupons = async() => {
  return (await msAPI.get("merchant/subscription/coupons")).data;
}

export const getStripeSubscriptions = async () => {
  return (await msAPI.get("merchant/admin/stripe-subscriptions")).data;
}

export const findStripeCustomer = async (search) => {
  return (await msAPI.post("merchant/admin/stripe-customers/find", { search })).data;
}

export const getStripeCustomerById = async (id) => {
  return (await msAPI.get(`merchant/admin/stripe-customers/${id}`)).data;
}

export const getStripeSubscriptionById = async(id) => {
  return (await msAPI.get(`merchant/admin/stripe-subscriptions/${id}`)).data;
}

export const findStripeSubscriptionByCustomerID = async (customer_id) => {
  return (await msAPI.post("merchant/admin/stripe-subscriptions/find", { customer_id })).data;
}

export const getStoreById = async (id) => {
  return (await msAPI.get(`merchant/admin/store/${id}`)).data;
};

export const getDSPProviders = async () => {
  return (await msAPI.get(`merchant/admin/dsp-providers`)).data;
};

export const getMarkets = async () => {
  return (await msAPI.get(`merchant/admin/markets`)).data;
};

export const getCityAndTeam = async (lat, lng) => {
  return (await msAPI.get(`merchant/city-and-team`, {
    params: { lat, lng },
  })).data;
};

export const createOrg = async (name, billing_email) => {
  return (await msAPI.post(`merchant/admin/org`, { name, billing_email })).data;
};

export const createSeller = async (data) => {
  return (await msAPI.post(`merchant/admin/seller`, data, {
    timeout: 60000, // 60 seconds
  })).data;
};

export const updateSeller = async (id, data) => {
  return (await msAPI.patch(`merchant/admin/seller/${id}`, data)).data;
};

export const patchUser = async (id, data) => {
  return (await msAPI.patch(`merchant/users/${id}`, data)).data;
};

export const inviteUser = async (data) => {
  return (await msAPI.post(`merchant/users`, data)).data;
};

export const getUsers = async () => {
  return (await msAPI.get(`merchant/users`)).data;
};

export const getAdmins = async () => {
  return (await msAPI.get(`merchant/admins`)).data;
}

export const getMerchantUsers = async (id) => {
  return (await msAPI.get(`merchant/admin/store/${id}/users`)).data;
};

export const createFTPCredential = async (id) => {
  return (await msAPI.post(`merchant/${id}/ftp-config`)).data;
};

export const updateFTPSettings = async (id, data) => {
  return (await msAPI.patch(`merchant/${id}/ftp-config`, data)).data;
};

export const updateOrg = async (id, data) => {
  return (await msAPI.patch(`merchant/admin/org/${id}`, data)).data;
};

export const getCartwheelTeams = async () => {
  return (await msAPI.get(`merchant/admin/cartwheel/teams`)).data;
}

export const getDashboardActions = async () => {
  return (await msAPI.get(`admin/dashboard/actions`)).data;
};

export const getIDScans = async () => {
  return (await msAPI.get(`admin/id-scan-review`)).data;
}

export const getIDScan = async (id) => {
  return (await msAPI.get(`admin/id-scan-review/${id}`)).data;
}

export const decideIDScan = async(id, data) => {
  return (await msAPI.patch(`admin/id-scan-review/${id}`, data)).data;
}

export const getPendingCustomProductComplianceReviews = async () => {
  return (await msAPI.get("admin/custom-product-compliance-reviews")).data;
};

/** Saucey admin: full catalog product details by SKU (management API). Returns the product doc. */
export const getManageProductDetails = async (sku) => {
  const body = (await manageAPI.get(`products/details/${encodeURIComponent(sku)}`)).data;
  return body?.data ?? body;
};

/**
 * Email a marketplace seller asking them to upload/update their license
 * documents (links them to Settings → Licenses).
 * @param {string} storeId - the seller's store_id
 * @param {string} email - recipient address (defaults to the seller's email in the admin UI)
 */
export const requestMarketplaceLicenses = async (storeId, email) => {
  const body = (
    await manageAPI.post(`merchant/admin/marketplace/${storeId}/request-licenses`, { email })
  ).data;
  return body?.data ?? body;
};

/** Saucey admin: unmatched retailer UPCs (management API). Params: page, limit, q, sort (`stores_desc` | `first_observed_desc`). */
export const getUnmatchedMasterMenuUPCs = async (params) => {
  return (
    await manageAPI.get("merchant/admin/master-menu/unmatched-upcs", {
      params,
    })
  ).data;
};

/** Saucey admin: cross-check UPCs against products_v2 and inventory_unmatched_feed. */
export const lookupDraftMasterMenuUPCs = async (upcs, { queueForAutocreate = true } = {}) => {
  return (
    await manageAPI.post("merchant/admin/master-menu/draft-products/upc-lookup", {
      upcs,
      queue_for_autocreate: queueForAutocreate,
    })
  ).data;
};

/** Saucey admin: draft master-menu products (management API). Params: page, limit, q, sort (`updated_desc` | `created_desc` | `title_asc`), ignored (`active` | `ignored` | `all`). */
export const getDraftMasterMenuProducts = async (params) => {
  return (
    await manageAPI.get("merchant/admin/master-menu/draft-products", {
      params,
    })
  ).data;
};

/** Saucey admin: single draft master-menu product by id. */
export const getDraftMasterMenuProduct = async (id) => {
  return (
    await manageAPI.get(
      `merchant/admin/master-menu/draft-products/${encodeURIComponent(id)}`
    )
  ).data;
};

/** Saucey admin: publish a draft master-menu product (MGMT coordinates Menu Service + cleanup). */
export const publishDraftMasterMenuProduct = async (id, product) => {
  return (
    await manageAPI.post(
      `merchant/admin/master-menu/draft-products/${encodeURIComponent(id)}/publish`,
      { product }
    )
  ).data;
};

/** Saucey admin: queue bulk publish for draft master-menu products. */
export const bulkPublishDraftMasterMenuProducts = async (
  draftIds,
  { ignoreRemaining = false, scopeQ = "", ignoreExemptDraftIds = [] } = {}
) => {
  return (
    await manageAPI.post("merchant/admin/master-menu/draft-products/bulk-publish", {
      draft_ids: draftIds,
      ignore_remaining: ignoreRemaining,
      scope_q: scopeQ || undefined,
      ignore_exempt_draft_ids: ignoreExemptDraftIds?.length ? ignoreExemptDraftIds : undefined,
    })
  ).data;
};

/** Saucey admin: ignore or restore draft master-menu products. */
export const bulkIgnoreDraftMasterMenuProducts = async (draftIds, ignored = true) => {
  return (
    await manageAPI.post("merchant/admin/master-menu/draft-products/bulk-ignore", {
      draft_ids: draftIds,
      ignored,
    })
  ).data;
};

/** Saucey admin: poll bulk publish job progress. */
export const getDraftMasterMenuPublishJob = async (jobId) => {
  return (
    await manageAPI.get(
      `merchant/admin/master-menu/draft-products/publish-jobs/${encodeURIComponent(jobId)}`
    )
  ).data;
};

export const getCustomProductComplianceReview = async (id) => {
  return (await msAPI.get(`admin/custom-product-compliance-reviews/${id}`)).data;
};

export const decideCustomProductComplianceReview = async (id, decision) => {
  return (
    await msAPI.patch(`admin/custom-product-compliance-reviews/${id}`, {
      decision,
    })
  ).data;
};

export const requestEmployeeDelivery = async () => {
  return (await msAPI.post(`merchant/employee-delivery-request`)).data;
}

export const saveCategoryRestrictions = async (id, data) => {
  return (await msAPI.patch(`merchant/admin/category-restrictions`, data)).data;
}

export const createMerchantStripeAccount = async(payload) => {
  return (await msAPI.post(`merchant/admin/merchant-stripe-customer`, payload)).data;
}

export const createSellerStripeAccount = async(payload) => {
  return (await msAPI.post(`merchant/admin/seller-stripe-customer`, payload)).data;
}

export const chargeAllFeesNow = async(orgID) => {
  return (await osAPI.get(`merchant/admin/charge-merchant-fees`, { params: { org: orgID } })).data;
}

export const createSellerManualPayout = async (sellerId, payload) => {
  return (
    await osAPI.post(`merchant/admin/seller/${sellerId}/manual-payout`, payload)
  ).data;
};

export const syncStoreStripeAccount = async(storeID) => {
  return (await msAPI.get(`merchant/store/${storeID}/sync-stripe-account`)).data;
}

export const forceSyncTypesense = async(easyID) => {
  return (await msAPI.get(`admin/${easyID}/build-typesense-index`)).data;
}

export const archiveStore = async(storeID) => {
  // /merchant/admin/merchant/:id/archive
  return (await msAPI.patch(`merchant/admin/seller/${storeID}/archive`)).data;
}

export const setProspectReadStatus = async (storeID, prospect_unread) => {
  return (
    await msAPI.patch(`merchant/admin/seller/${storeID}/prospect-read`, {
      prospect_unread,
    })
  ).data;
};

export const archiveMerchant = async(merchantID) => {
  return (await msAPI.patch(`merchant/admin/merchant/${merchantID}/archive`)).data;
}

/**
 * Approve onboard steps for a prospect merchant
 * @param {string} storeId - The store ID
 * @returns {Promise<{data: Object}>}
 */
export const approveOnboardSteps = async (storeId) => {
  return (await msAPI.post(`merchant/admin/prospect/${storeId}/approve`)).data;
};

/**
 * Reject onboard steps for a prospect merchant
 * @param {string} storeId - The store ID
 * @param {string} reason - Rejection reason (will be emailed to user)
 * @returns {Promise<{data: Object}>}
 */
export const rejectOnboardSteps = async (storeId, reason) => {
  return (await manageAPI.post(`merchant/admin/seller/${storeId}/onboard-steps/reject`, { reason })).data;
};

/**
 * Get business documents for a specific store (admin view)
 * @param {string} storeId - The store ID
 * @returns {Promise<{data: Array}>}
 */
export const getStoreBusinessDocuments = async (storeId) => {
  return (await manageAPI.get(`merchant/${storeId}/business-documents`)).data;
};

/**
 * Get Stripe account management token for a specific store
 * @param {string} storeId - The store ID
 * @returns {Promise<{client_secret: string}>}
 */
export const getStripeAccountManagementToken = async (storeId) => {
  return (await msAPI.get(`merchant/admin/stripe-token/${storeId}/account-management`)).data;
};

export const publishStore = async () => {
  return (await msAPI.patch(`merchant/seller/publish`)).data;
}

/**
 * Bulk-create marketplace sellers from a parsed CSV.
 * @param {Array<Object>} rows - Each row must include the required CSV columns.
 * @returns {Promise<{created: Array<{store_id: string, name: string}>, errors: Array<{row: number, reason: string, data: Object}>}>}
 */
export const bulkCreateMarketplaceSellers = async (rows) => {
  const body = (await manageAPI.post(`merchant/admin/marketplace/bulk-create`, { rows }, {
    timeout: 120000,
  })).data;
  // BE wraps the payload in {data, message, status, version}; unwrap so callers
  // can read .created / .errors directly. Falls back to body when unwrapped.
  return body?.data ?? body;
};
